import type { LineSupply, SaleSupplyLine, SupplyMode } from './supply.api'

/** The four choices, in the order the screens offer them (maquette S11). */
export const SUPPLY_MODES: { mode: SupplyMode; label: string }[] = [
  { mode: 'buy', label: 'Acheter' },
  { mode: 'make', label: 'Fabriquer' },
  { mode: 'none', label: 'Aucun' },
  { mode: 'unqualified', label: 'À préciser' },
]

export type SupplyTone = 'buy' | 'warn' | 'make' | 'none' | 'lock'

/**
 * What the Appro cell says. Never a colour alone (design §Vocabulaire): « fournisseur à préciser » and
 * « À préciser » are words first, amber second.
 */
export function supplyLabel(supply: Pick<LineSupply, 'mode' | 'supplierId'>, supplierName?: string | null): string {
  switch (supply.mode) {
    case 'buy':
      return supply.supplierId ? `Acheter · ${supplierName ?? 'fournisseur supprimé'}` : 'Acheter · fournisseur à préciser'
    case 'make':
      return 'Fabriquer'
    case 'none':
      return 'Aucun'
    default:
      return 'À préciser'
  }
}

export function supplyTone(supply: Pick<LineSupply, 'mode' | 'supplierId' | 'locked'>): SupplyTone {
  if (supply.locked) return 'lock'
  if (supply.mode === 'unqualified' || (supply.mode === 'buy' && !supply.supplierId)) return 'warn'
  if (supply.mode === 'buy') return 'buy'
  if (supply.mode === 'make') return 'make'
  return 'none'
}

export interface SupplySummary {
  lineCount: number
  toBuy: number
  supplierCount: number
  missingSupplier: number
  unqualified: number
  noPurchase: number
}

/** The sale header: « 52 lignes · 46 à acheter chez 4 fournisseurs · 3 fournisseur à préciser · 3 sans achat ». */
export function summarizeSupply(lines: Pick<SaleSupplyLine, 'supply'>[]): SupplySummary {
  const buy = lines.filter((l) => l.supply.mode === 'buy')
  return {
    lineCount: lines.length,
    toBuy: buy.length,
    supplierCount: new Set(buy.map((l) => l.supply.supplierId).filter(Boolean)).size,
    missingSupplier: buy.filter((l) => !l.supply.supplierId).length,
    unqualified: lines.filter((l) => l.supply.mode === 'unqualified').length,
    noPurchase: lines.filter((l) => l.supply.mode === 'none' || l.supply.mode === 'make').length,
  }
}

const qty = (n: number) => n.toLocaleString('fr-BE', { maximumFractionDigits: 4 })

/**
 * « vendu 1, Winner : 4 m » when the file counted the line another way than the sale does — the
 * quantity to order is the quantity sold (decision 12), the Winner one stays visible here.
 */
export function originQuantityHint(line: Pick<SaleSupplyLine, 'quantity' | 'originQuantity' | 'originUnit'>): string | null {
  if (line.originQuantity == null || line.quantity == null) return null
  if (line.originQuantity === line.quantity && !line.originUnit) return null
  const winner = `${qty(line.originQuantity)}${line.originUnit ? ` ${line.originUnit}` : ''}`
  if (line.originQuantity === line.quantity && ['pc', 'pce', 'st', 'stk'].includes(line.originUnit!.toLowerCase())) return null
  return `vendu ${qty(line.quantity)}, Winner : ${winner}`
}
